import type { AnchorHTMLAttributes, ReactElement } from "react";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden";
import styles from "./Link.module.css";

export type LinkVariant = "default" | "nav";

export interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  variant?: LinkVariant;
  /**
   * Opens in a new tab with a safe `rel` and a screen-reader-announced hint.
   * Use sparingly — new tabs should be a deliberate, signposted choice.
   */
  external?: boolean;
}

/**
 * Styled anchor. Real `<a>` with token-driven colours and a visible focus ring
 * (from the global :focus-visible rule). External links get
 * `rel="noopener noreferrer"` and an accessible "opens in a new tab" hint.
 */
export function Link({
  variant = "default",
  external = false,
  className,
  children,
  target,
  rel,
  ...rest
}: LinkProps): ReactElement {
  const classes = [styles.link, variant === "nav" ? styles.nav : undefined, className]
    .filter(Boolean)
    .join(" ");
  const resolvedTarget = external ? "_blank" : target;
  const resolvedRel = external ? "noopener noreferrer" : rel;
  return (
    <a className={classes} target={resolvedTarget} rel={resolvedRel} {...rest}>
      {children}
      {external ? <VisuallyHidden> (opens in a new tab)</VisuallyHidden> : null}
    </a>
  );
}
